import { useEffect, useState } from "react";
import axios from "axios";
import { ArrowRight, ShieldCheck, Truck, Award, Shirt, Footprints, CircleDot, Hand, ShoppingBag } from "lucide-react";
import { Link } from "react-router-dom";
import ProductCard from "@/components/site/ProductCard";
import { useStore } from "@/lib/store";
import { translations, CATEGORY_LABELS } from "@/lib/i18n";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ICONS = { Shirt, Footprints, CircleDot, Hand, ShoppingBag };
function CategoryIcon({ name }) {
  const Icon = ICONS[name] || ShoppingBag;
  return (
    <Icon
      className="w-16 h-16 text-yellow-400 group-hover:scale-110 transition-transform duration-500 relative z-10"
      strokeWidth={1.4}
      style={{ filter: "drop-shadow(0 8px 18px rgba(212,175,55,0.5))" }}
    />
  );
}

export default function Home() {
  const { lang } = useStore();
  const t = translations[lang];
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/products`, { params: { featured: true, limit: 8 } })
      .then((r) => setFeatured(r.data.items || []));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const categories = [
    { key: "jerseys_countries" },
    { key: "jerseys_clubs" },
    { key: "jerseys_neutral" },
    { key: "cleats" },
    { key: "shoes" },
    { key: "accessories" },
  ];

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <div
            className="absolute inset-0"
            style={{
              background:
                "radial-gradient(800px 500px at 80% 30%, rgba(212,175,55,0.18), transparent 60%), radial-gradient(900px 600px at 10% 80%, rgba(212,175,55,0.08), transparent 55%), #050505",
            }}
          />
        </div>

        <div className="max-w-7xl mx-auto px-6 pt-16 lg:pt-24 pb-12 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <p
              className="text-xs tracking-[0.5em] text-yellow-400/80 mb-6"
              style={{ fontFamily: '"JetBrains Mono", monospace' }}
            >
              {t.hero_eyebrow}
            </p>
            <h1
              className="text-5xl sm:text-6xl lg:text-7xl leading-[0.95] font-black"
              style={{ fontFamily: '"Bebas Neue", system-ui', letterSpacing: "0.01em" }}
            >
              <span className="block text-white">{t.hero_title_1}</span>
              <span
                className="block"
                style={{
                  background: "linear-gradient(180deg, #F8E27A 0%, #D4AF37 50%, #8B6914 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  filter: "drop-shadow(0 6px 16px rgba(212,175,55,0.4))",
                }}
              >
                {t.hero_title_2}
              </span>
            </h1>
            <p className="mt-7 text-lg text-white/70 max-w-xl">{t.hero_subtitle}</p>

            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link
                to="/shop"
                data-testid="hero-cta-shop"
                className="px-7 py-3.5 rounded-full font-bold tracking-widest text-black inline-flex items-center gap-2 hover:scale-[1.02] transition-transform"
                style={{
                  background: "linear-gradient(180deg, #F4D77A, #D4AF37)",
                  boxShadow: "0 12px 32px -8px rgba(212,175,55,0.55), inset 0 1px 0 rgba(255,255,255,0.5)",
                }}
              >
                {t.hero_cta_shop}
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/lookbook"
                data-testid="hero-cta-lookbook"
                className="px-7 py-3.5 rounded-full font-bold tracking-widest border border-yellow-500/40 text-yellow-200 hover:border-yellow-300 hover:text-yellow-100 transition-colors"
              >
                {t.hero_cta_lookbook}
              </Link>
            </div>

            <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3 text-xs text-white/55 tracking-wider uppercase">
              <span className="flex items-center gap-2"><Truck className="w-4 h-4 text-yellow-400" /> {t.free_shipping}</span>
              <span className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-yellow-400" /> {t.secure_order}</span>
              <span className="flex items-center gap-2"><Award className="w-4 h-4 text-yellow-400" /> {t.pay_on_delivery}</span>
            </div>
          </div>

          <div className="relative flex justify-center items-center">
            <div
              className="absolute inset-10 rounded-full"
              style={{
                background: "radial-gradient(circle, rgba(212,175,55,0.35), transparent 60%)",
                filter: "blur(40px)",
              }}
            />
            <img
              src="/logos/braf_zak_logo_main.jpeg"
              alt="Braf_Zak logo"
              data-testid="hero-logo"
              className="relative w-[90%] max-w-[520px] aspect-square object-cover rounded-3xl"
              style={{
                boxShadow:
                  "0 30px 80px -20px rgba(212,175,55,0.4), 0 0 0 1px rgba(212,175,55,0.25)",
              }}
            />
          </div>
        </div>
      </section>

      {/* FEATURED */}
      {featured.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 py-20">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
            <div>
              <p
                className="text-xs tracking-[0.4em] text-yellow-400/80 mb-3"
                style={{ fontFamily: '"JetBrains Mono", monospace' }}
              >
                {t.featured_eyebrow}
              </p>
              <h2
                className="text-4xl sm:text-5xl font-black text-white"
                style={{ fontFamily: '"Bebas Neue", system-ui' }}
              >
                {t.featured_title}
              </h2>
            </div>
            <Link to="/shop" className="text-yellow-300 text-sm tracking-widest hover:text-yellow-200 inline-flex items-center gap-2 uppercase">
              {t.nav_shop} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {featured.slice(0, 4).map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        </section>
      )}

      {/* CATEGORIES */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <h2
          className="text-3xl sm:text-4xl font-black text-white mb-10"
          style={{ fontFamily: '"Bebas Neue", system-ui' }}
        >
          {t.categories_title}
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {categories.map((cat, i) => {
            const Icons = {
              jerseys: "Shirt",
              cleats: "Footprints",
              shoes: "Footprints",
              training: "Target",
              accessories: "ShoppingBag",
            };
            return (
              <Link
                key={cat.key}
                to={`/shop?category=${cat.key}`}
                data-testid={`category-tile-${cat.key}`}
                className="relative aspect-[3/4] rounded-2xl overflow-hidden border border-yellow-500/15 hover:border-yellow-400/60 transition-all group flex flex-col items-center justify-center"
                style={{
                  background:
                    "radial-gradient(120% 80% at 30% 20%, #1a1408 0%, #050505 70%)",
                  animation: `fadeUp 0.6s ease ${0.05 * i}s both`,
                }}
              >
                <div
                  className="absolute inset-0 opacity-[0.06] group-hover:opacity-[0.12] transition-opacity"
                  style={{
                    backgroundImage:
                      "linear-gradient(#D4AF37 1px, transparent 1px), linear-gradient(90deg, #D4AF37 1px, transparent 1px)",
                    backgroundSize: "32px 32px",
                  }}
                />
                <CategoryIcon name={Icons[cat.key]} />
                <p className="text-yellow-300 text-xs tracking-[0.3em] uppercase mt-4">
                  {String(i + 1).padStart(2, "0")}
                </p>
                <h3
                  className="text-white font-black text-2xl mt-1 group-hover:text-yellow-200 transition-colors"
                  style={{ fontFamily: '"Bebas Neue", system-ui', letterSpacing: "0.04em" }}
                >
                  {CATEGORY_LABELS[cat.key][lang]}
                </h3>
              </Link>
            );
          })}
        </div>
      </section>

      {/* STORY */}
      <section className="relative py-24 overflow-hidden">
        <div
          className="absolute inset-0 -z-10"
          style={{
            background:
              "linear-gradient(180deg, transparent, rgba(212,175,55,0.05), transparent), #050505",
          }}
        />
        <div className="max-w-5xl mx-auto px-6 text-center">
          <p
            className="text-xs tracking-[0.5em] text-yellow-400/80 mb-5"
            style={{ fontFamily: '"JetBrains Mono", monospace' }}
          >
            {t.story_eyebrow}
          </p>
          <h2
            className="text-4xl sm:text-6xl font-black mb-7"
            style={{
              fontFamily: '"Bebas Neue", system-ui',
              background: "linear-gradient(180deg, #fff 0%, #F4D77A 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            {t.story_title}
          </h2>
          <p className="text-lg text-white/65 max-w-3xl mx-auto leading-relaxed">{t.story_body}</p>
          <Link
            to="/about"
            data-testid="story-cta"
            className="inline-flex items-center gap-2 mt-8 px-6 py-3 rounded-full text-sm font-bold tracking-widest border border-yellow-500/40 text-yellow-200 hover:border-yellow-300 transition-colors"
          >
            {t.story_cta} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
